import { PermissionGate } from "@/components/common/PermissionGate"
import { Button } from "@/components/ui/button"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { PERMISSIONS } from "@/constants/permissions"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { Pencil, Plus } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { useNavigate, useSearchParams } from "react-router"
import { toastApiError } from "../../../api/apiError"
import {
  useArticleSubstances,
  useUpdateArticleSubstance,
} from "../../../api/articlesApi"
import type { ArticleSubstanceOut } from "../../../api/types/masterData"
import {
  EntitySubTable,
  type SubTableColumn,
} from "../../shared/EntitySubTable"
import { McPill } from "../../shared/McPill"
import { UnlinkArticleSubstanceButton } from "../../shared/UnlinkArticleSubstanceButton"
import { LinkSubstanceDialog } from "../substances/LinkSubstanceDialog"

// `editing` is owned by the section, not the cell: linking a substance that is
// already linked answers 409 with the existing link's id, and the only useful
// next action is to open THAT row's concentration. A cell that owned its own
// flag could never be opened from outside.
/** Bounds -> the text a reader sees. The comparator is DERIVED, never stored, so
 *  it can never disagree with the numbers it describes — the same rule the PDF
 *  renderer follows (`mc_tools/concentration.format_concentration`). */
export function formatConcentration(
  min: number | null | undefined,
  max: number | null | undefined,
  fallback: number | null | undefined,
): string {
  if (min == null && max == null)
    return fallback == null ? "—" : `${fallback}% w/w`
  if (max == null) return `> ${min}% w/w`
  if (min == null) return `< ${max}% w/w`
  if (min === max) return `${min}% w/w`
  return `${min} – ${max}% w/w`
}

/** How the supplier stated it. "exactly" is a measurement; "atLeast" is the
 *  ordinary answer a distributor gets ("> 0,1 %"), which has no known ceiling
 *  and could not be entered at all while this was a plain number box. */
type Comparator = "exactly" | "atLeast" | "atMost"

function comparatorOf(bounds: {
  concentrationMinPct: number | null
  concentrationMaxPct: number | null
}): Comparator {
  if (bounds.concentrationMaxPct == null && bounds.concentrationMinPct != null)
    return "atLeast"
  if (bounds.concentrationMinPct == null && bounds.concentrationMaxPct != null)
    return "atMost"
  return "exactly"
}

function boundsFor(comparator: Comparator, amount: number) {
  if (comparator === "atLeast")
    return { concentrationMinPct: amount, concentrationMaxPct: null }
  if (comparator === "atMost")
    return { concentrationMinPct: null, concentrationMaxPct: amount }
  return { concentrationMinPct: amount, concentrationMaxPct: amount }
}

function ConcentrationCell({
  row,
  editing,
  onEditingChange,
}: {
  row: ArticleSubstanceOut
  editing: boolean
  onEditingChange: (editing: boolean) => void
}) {
  const { translate } = useTranslation()
  const { mutate, isPending } = useUpdateArticleSubstance()
  const [value, setValue] = useState("")
  const [comparator, setComparator] = useState<Comparator>("exactly")
  const committedRef = useRef(false)
  const setEditing = onEditingChange

  // Seed the field whenever the row opens, not only on click: opened from the
  // outside (?edit=, or the dialog's 409 offer) it would otherwise start blank
  // and a commit would read as "clear the concentration".
  // Depends on the three SCALARS, not on `row`: the substances list refetches
  // after any mutation in the section, `row` is a new object each time, and
  // depending on it re-seeded the field out from under whoever was typing.
  const { concentrationPct, concentrationMinPct, concentrationMaxPct } = row
  useEffect(() => {
    if (!editing) return
    committedRef.current = false
    setComparator(comparatorOf({ concentrationMinPct, concentrationMaxPct }))
    const stated = concentrationMinPct ?? concentrationMaxPct
    setValue((stated ?? concentrationPct)?.toString() ?? "")
  }, [editing, concentrationPct, concentrationMinPct, concentrationMaxPct])

  if (!editing) {
    return (
      <button
        type="button"
        className="font-mono group flex w-full items-center gap-1.5"
        onClick={(e) => {
          e.stopPropagation()
          setEditing(true)
        }}
      >
        <span className="group-hover:underline">
          {formatConcentration(
            row.concentrationMinPct,
            row.concentrationMaxPct,
            row.concentrationPct,
          )}
        </span>
        <Pencil
          size={12}
          className="text-grey5 opacity-0 group-hover:opacity-100"
        />
      </button>
    )
  }

  function commit() {
    // Enter commits and closes; closing unmounts the inputs, and the resulting
    // blur would run this a second time and fire a second mutation. One latch
    // per opening — reset when the cell reopens, in the seeding effect above.
    if (committedRef.current) return
    committedRef.current = true
    setEditing(false)
    const trimmed = value.trim()
    if (trimmed === "") return
    const parsed = Number(trimmed)
    if (!Number.isFinite(parsed)) return
    const bounds = boundsFor(comparator, parsed)
    // Compare against the BOUNDS, not the single number: switching "exactly 0,1"
    // to "> 0,1" leaves that number unchanged and is still a real edit — the one
    // the reviewer opened the cell to make.
    if (
      bounds.concentrationMinPct === row.concentrationMinPct &&
      bounds.concentrationMaxPct === row.concentrationMaxPct
    )
      return
    mutate(
      {
        articleId: row.articleId,
        linkId: row.id,
        changes: bounds,
      },
      {
        onError: (err) => {
          toastApiError(err, translate, "MC_MD_SAVE_ERROR")
        },
      },
    )
  }

  // Two controls, because "> 0,1 %" is two facts. A supplier almost never states
  // an exact figure, so a bare number box left the reviewer inventing one above
  // the threshold to get the substance onto the document at all (F16).
  const keys = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") commit()
    if (e.key === "Escape") setEditing(false)
  }
  return (
    <div
      className="flex items-center gap-1"
      onClick={(e) => e.stopPropagation()}
      onBlur={(e) => {
        // Only when focus leaves the pair — moving between the two is not a commit.
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) commit()
      }}
    >
      <select
        className="h-6 rounded border border-grey4 bg-grey3 px-1 text-xs text-white outline-hidden focus:border-yellow1"
        aria-label={translate("MC_MD_CONC_COMPARATOR")}
        value={comparator}
        disabled={isPending}
        onChange={(e) => setComparator(e.target.value as Comparator)}
        onKeyDown={keys}
      >
        <option value="exactly">=</option>
        <option value="atLeast">&gt;</option>
        <option value="atMost">&lt;</option>
      </select>
      <input
        autoFocus
        type="number"
        step="any"
        className="font-mono h-6 w-16 rounded border border-grey4 bg-grey3 px-1.5 text-xs text-white outline-hidden focus:border-yellow1"
        value={value}
        disabled={isPending}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={keys}
      />
    </div>
  )
}

type FrameworkEntry = {
  label: string
  detail: string | null
}

function useRestrictedFrameworks() {
  const { translate } = useTranslation()
  return (s: ArticleSubstanceOut): FrameworkEntry[] => {
    const entries: FrameworkEntry[] = []
    if (s.svhcListed)
      entries.push({
        label: translate("MC_MD_FRAMEWORK_SVHC_REACH"),
        detail: s.svhcThreshold,
      })
    if (s.rohsListed)
      entries.push({
        label: translate("MC_MD_FRAMEWORK_ROHS"),
        detail: s.rohsThreshold,
      })
    if (s.annexXviiListed)
      entries.push({
        label: translate("MC_MD_FRAMEWORK_REACH_ANNEX_XVII"),
        detail: s.annexXviiThreshold,
      })
    if (s.popsListed)
      entries.push({
        label: translate("MC_MD_FRAMEWORK_POPS"),
        detail: s.popsThreshold,
      })
    if (s.annexXivListed)
      entries.push({
        label: translate("MC_MD_FRAMEWORK_REACH_ANNEX_XIV"),
        detail: s.annexXivSunset,
      })
    if (s.cmrListed)
      entries.push({
        label: translate("MC_MD_FRAMEWORK_CMR"),
        detail: s.cmrClassification,
      })
    return entries
  }
}

function RestrictedCell({ row }: { row: ArticleSubstanceOut }) {
  const { translate } = useTranslation()
  const getRestrictedFrameworks = useRestrictedFrameworks()

  if (!row.isRestricted) {
    return <span className="text-grey5">—</span>
  }

  const frameworks = getRestrictedFrameworks(row)
  const pill = row.exemption ? (
    <McPill tone="attention">{translate("MC_MD_PANEL_BELOW_THRESHOLD")}</McPill>
  ) : (
    <McPill tone="attention">{translate("MC_MD_PILL_RESTRICTED")}</McPill>
  )

  if (frameworks.length === 0) return pill

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="cursor-help">{pill}</span>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="max-w-xs border border-grey4 bg-grey2 px-3 py-2"
        >
          <p className="mb-1.5 text-[11px] font-medium text-grey5">
            {translate("MC_MD_TOOLTIP_RESTRICTED_UNDER")}
          </p>
          <ul className="space-y-0.5">
            {frameworks.map((f) => (
              <li key={f.label} className="text-[11.5px] text-white">
                <span className="font-medium">{f.label}</span>
                {f.detail && (
                  <span className="ml-1 text-grey5">— {f.detail}</span>
                )}
              </li>
            ))}
          </ul>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export function ArticleSubstancesSection() {
  const [searchParams, setSearchParams] = useSearchParams()
  const id = searchParams.get("id")
  // Set by the substance page's own "already linked" path, which cannot open
  // the row itself: the concentration editor only exists here.
  const editParam = searchParams.get("edit")
  const navigate = useNavigate()
  const { translate } = useTranslation()
  const canEdit = useFeatureFlag("MC_MASTER_DATA_SUBSTANCE_EDIT")
  const [dialogOpen, setDialogOpen] = useState(false)
  // Which link row has its concentration open for editing, if any.
  const [editingLinkId, setEditingLinkId] = useState<string | null>(editParam)
  // Consume `?edit=` once: it is a one-shot instruction, and leaving it in the
  // URL would reopen the editor on every later render and survive a reload.
  useEffect(() => {
    if (!editParam) return
    setEditingLinkId(editParam)
    const next = new URLSearchParams(searchParams)
    next.delete("edit")
    setSearchParams(next, { replace: true })
  }, [editParam, searchParams, setSearchParams])

  const { data: substances = [], isLoading } = useArticleSubstances(id)

  const columns: SubTableColumn<ArticleSubstanceOut>[] = [
    {
      key: "name",
      header: translate("MC_MD_PANEL_COL_SUBSTANCE"),
      cell: (s) =>
        s.substanceName ?? s.casNumber ?? s.ecNumber ?? s.substanceId,
    },
    {
      key: "cas",
      header: translate("MC_MD_PANEL_COL_CAS"),
      cell: (s) =>
        s.casNumber ? (
          <span className="font-mono">{s.casNumber}</span>
        ) : (
          <span className="text-grey5">—</span>
        ),
      className: "w-28",
      hideBelow: "360px",
    },
    {
      key: "ec",
      header: translate("MC_MD_FIELD_EC_NUMBER"),
      cell: (s) =>
        s.ecNumber ? (
          <span className="font-mono">{s.ecNumber}</span>
        ) : (
          <span className="text-grey5">—</span>
        ),
      className: "w-28",
      hideBelow: "480px",
    },
    {
      key: "supplier",
      header: translate("MC_MD_PANEL_COL_SUPPLIER"),
      cell: (s) =>
        s.supplierName ? (
          <span className="truncate">{s.supplierName}</span>
        ) : (
          <span className="italic text-grey5">
            {translate("MC_MD_IMPORTED_LABEL")}
          </span>
        ),
      className: "w-32",
    },
    {
      key: "concentration",
      header: translate("MC_MD_PANEL_COL_CONC"),
      cell: (s) => (
        <ConcentrationCell
          row={s}
          editing={editingLinkId === s.id}
          onEditingChange={(on) => setEditingLinkId(on ? s.id : null)}
        />
      ),
      className: "w-32",
    },
    {
      key: "restricted",
      header: translate("MC_MD_PANEL_COL_RESTRICTED"),
      cell: (s) => <RestrictedCell row={s} />,
      className: "w-32",
    },
  ]

  if (canEdit) {
    columns.push({
      key: "unlink",
      header: "",
      cell: (s) => (
        <PermissionGate permission={PERMISSIONS.MC_MASTER_DATA_EDIT}>
          <UnlinkArticleSubstanceButton
            articleId={s.articleId}
            linkId={s.id}
            source={s.source}
            itemLabel={s.substanceName ?? s.substanceId}
          />
        </PermissionGate>
      ),
      className: "w-10 text-right",
    })
  }

  const addButton = canEdit ? (
    <PermissionGate permission={PERMISSIONS.MC_MASTER_DATA_EDIT}>
      <Button variant="outline" size="sm" onClick={() => setDialogOpen(true)}>
        <Plus />
        {translate("MC_MD_PANEL_SUBSTANCES_LINK")}
      </Button>
    </PermissionGate>
  ) : undefined

  return (
    <>
      <EntitySubTable
        title={translate("MC_MD_PANEL_SUBSTANCES_TITLE")}
        items={substances}
        columns={columns}
        keyOf={(s) => s.id}
        hasMore={false}
        onFetchMore={() => Promise.resolve()}
        isLoading={isLoading}
        total={isLoading ? undefined : substances.length}
        onRowClick={(s) =>
          navigate(`../substances?id=${s.substanceId}&source=nav`)
        }
        action={addButton}
      />
      {canEdit && (
        <LinkSubstanceDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          articleId={id ?? undefined}
          // Redundant with onOpenChange(false) inside the dialog's own success
          // handler today, but kept as the extension point for anything this
          // section wants to do specifically on a successful link later (a
          // toast, scrolling the new row into view, …) without touching the
          // dialog itself.
          onLinked={() => setDialogOpen(false)}
          // Same destination the rows themselves navigate to, so "already linked"
          // ends at the entry instead of a dead form (F14).
          // The 409 hands back the link that already exists, so land on that
          // row with its concentration open — the edit the user came to make.
          // Navigating to the substance instead just moved the dead end (F14).
          onExistingLink={(linkId) => {
            setDialogOpen(false)
            setEditingLinkId(linkId)
          }}
        />
      )}
    </>
  )
}
